/**
 * Analyzer: Day Baseline Comparison
 *
 * Compares today's P&L to the user's historical daily average. Requires
 * ≥5 trading days of history to avoid comparing against a tiny sample.
 *
 * Note: drawdown was previously inside this analyzer, but it's a free-tier
 * risk signal that has nothing to do with the baseline. Moved to a dedicated
 * `dayDrawdown` analyzer so free users see it.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';

export const key = 'dayBaselineCompare';
// Pro-gated: comparing today's P&L against a rolling historical baseline is
// one of the headline "tell me if today was normal for me" signals and is the
// cleanest first Pro analyzer. Free-tier users still see the raw day grade,
// mistake clusters, setup/ATM spotlights, drawdown, etc. — they just don't
// get the statistical baseline lens.
export const tier = 'pro';

export const analyze = (ctx) => {
  const { day } = ctx;
  if (!day || !day.core) return null;
  // Live-trade gate: skip when no trades have closed today. v7.5.14 contract
  // extended to day-scope; see dayUtils.js for the closed-only core stats.
  if (day.core.closedTotal === 0) return null;

  const out = [];
  const { totalPnL } = day.core;
  const baseline = day.baseline;

  // Historical comparison — needs ≥5 trading days
  if (!baseline || baseline.tradingDays < 5) return null;

  const avg = baseline.avgDailyPnL;
  const diff = totalPnL - avg;

  if (avg > 0 && totalPnL > avg * 1.5) {
    const pctAbove = ((totalPnL / avg - 1) * 100).toFixed(0);
    out.push(makeObservation({
      id: `${key}:${day.date}:above`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.VARIANCE,
      title: 'Above-average day',
      detail: `Today's P&L exceeded your daily average by ${pctAbove}%.`,
      action: 'Capture what went right in your EOD notes — setup, size, timing — so it stops being an accident.',
      evidence: { totalPnL, avgDailyPnL: avg, diff },
      metric: { label: 'vs Avg', value: `+${pctAbove}%`, unit: '' },
      confidence: 0.75,
      priority: 50
    }));
  } else if (avg <= 0 && totalPnL > 0) {
    out.push(makeObservation({
      id: `${key}:${day.date}:flip`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.VARIANCE,
      title: 'Beat your slump',
      detail: `Today's +$${totalPnL.toFixed(0)} topped your daily avg of ${avg >= 0 ? '+' : '−'}$${Math.abs(avg).toFixed(0)}.`,
      action: 'Write one sentence on what changed today vs the slump — that sentence is your next edge.',
      evidence: { totalPnL, avgDailyPnL: avg },
      confidence: 0.75,
      priority: 55
    }));
  } else if (avg > 0 && totalPnL < avg * 0.5 && totalPnL < 0) {
    // Positive-avg user had a clearly below-average red day
    out.push(makeObservation({
      id: `${key}:${day.date}:below`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.WARNING,
      category: CATEGORY.VARIANCE,
      title: 'Below-average day',
      detail: `Today was $${Math.abs(diff).toFixed(0)} worse than your daily average. Review what was different.`,
      action: 'Scan setups + time-of-day + mistakes. The gap usually lives in one of those three.',
      evidence: { totalPnL, avgDailyPnL: avg, diff },
      confidence: 0.7,
      priority: 60
    }));
  } else if (avg < 0 && totalPnL < avg * 1.5) {
    // Symmetric branch: user already in a slump, today is clearly worse
    // than their already-bad average. (avg * 1.5 because avg is negative —
    // multiplying by 1.5 makes it MORE negative, e.g. avg=-100 → threshold=-150,
    // so totalPnL < -150 fires.)
    out.push(makeObservation({
      id: `${key}:${day.date}:below`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.WARNING,
      category: CATEGORY.VARIANCE,
      title: 'Worse than an already-rough baseline',
      detail: `Today was $${Math.abs(diff).toFixed(0)} below your already-negative average of −$${Math.abs(avg).toFixed(0)}. The slump is deepening.`,
      action: 'Step back before the next session — review what changed and consider a size cut or paper-trading day.',
      evidence: { totalPnL, avgDailyPnL: avg, diff },
      confidence: 0.7,
      priority: 65
    }));
  }

  return out.length > 0 ? out : null;
};
